import { Auth } from '@aws-amplify/auth';
import axios, { AxiosError } from 'axios';

import i18n from '@/core/i18n';
import { displayToastInfo } from '@/helpers/display-toast';
import { BackendErrorCode } from '@/modules/common/api/errors/backend-error-codes';
import { AuthenticatedRoutes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

import { isAuthenticated, requestLogout } from './auth-actor';
import { baseURL } from './axios.config';
import { navigateTo } from './navigation';

const TAG = 'AxiosInstance:';

export const axiosInstance = axios.create({
  baseURL,
});

/**
 * Session lookup currently in flight, shared by concurrent requests.
 *
 * `amazon-cognito-identity-js` does not dedupe refreshes — every caller of
 * `Auth.currentSession()` with an expired token fires its own Cognito
 * `REFRESH_TOKEN_AUTH`. On a long-idle tab one user action fans out into a burst
 * of parallel requests, so they all refresh at once and race to write the same
 * localStorage keys. Cleared on settle, so this only collapses a burst.
 */
let inFlightSession: Promise<Awaited<ReturnType<typeof Auth.currentSession>>> | null = null;

const getAccessToken = async (): Promise<string | null> => {
  inFlightSession ??= Auth.currentSession().finally(() => {
    inFlightSession = null;
  });

  try {
    const session = await inFlightSession;
    return session.getAccessToken().getJwtToken();
  } catch (error) {
    // Expected on public pages; a real problem only if we think we're signed in.
    if (isAuthenticated()) logger.warn(TAG, 'Could not attach an access token', { error });
    return null;
  }
};

// Attach a fresh access token to every outgoing request.
//
// We delegate expiration checks entirely to Amplify: `Auth.currentSession()`
// reads the cached session and refreshes it automatically when the access
// token is expired. Comparing the token's `exp` against the client's clock
// (as a previous version of this interceptor did) is unsafe — if the user's
// system clock is ahead of server time, even a freshly issued token looks
// expired locally, which caused an infinite refresh loop flooding Cognito
// and every authenticated endpoint.
axiosInstance.interceptors.request.use(async config => {
  const accessToken = await getAccessToken();
  // No token: proceed unauthenticated and let the response interceptor handle the 401.
  if (accessToken) config.headers.authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * Maps backend error codes to full-page redirect routes.
 *
 * When a backend error code listed here is returned by ANY endpoint, the axios
 * response interceptor below performs a client-side navigation to the mapped route
 * instead of letting the error propagate to the calling component.
 *
 * ## Adding a new global redirect error
 *
 * 1. Add the backend error code to `BackendErrorCode` in `backend-error-codes.ts`
 * 2. Add an entry here: `[BackendErrorCode.YOUR_CODE]: AuthenticatedRoutes.YourPage`
 * 3. Create the page component and register its route in `app-router.tsx`
 * 4. Add i18n copy to `public/locales/en/common.json`
 *
 * **IMPORTANT — onboarding machine special case:**
 * The onboarding machine calls `getNotPassedOnboardingRoute` directly (outside TanStack
 * Query), so its catch block also needs to know which errors are handled here and should
 * NOT be forwarded to the machine's own error state. It reads `GLOBAL_REDIRECT_ERROR_CODES`
 * (exported below) to stay in sync automatically — you do NOT need to touch the machine.
 */
export const GLOBAL_REDIRECT_BACKEND_CODES: Partial<Record<BackendErrorCode, AuthenticatedRoutes>> =
  {
    [BackendErrorCode.ACCESS_ERROR_NO_ACTIVE_SUBSCRIPTIONS_FOUND]:
      AuthenticatedRoutes.BillingExpired,
  };

/**
 * Tracks whether we've already reacted to a 401 in the current session.
 *
 * When the access token is rejected by the backend (refresh token dead, token
 * revoked, or clock skew beyond Cognito's tolerance), every in-flight request
 * will fail with 401 in parallel. We only want to show the toast and trigger
 * logout once per "session death" — not N times. Reset on the next successful
 * response so a subsequent session death (after the user signs back in and
 * drifts again) still fires.
 */
let sessionExpiredHandled = false;

axiosInstance.interceptors.response.use(
  response => {
    sessionExpiredHandled = false;
    return response;
  },
  (error: AxiosError) => {
    const status = error?.response?.status;
    const backendCode = (error?.response?.data as { error?: { code?: BackendErrorCode } })?.error
      ?.code;

    if (status === 401 && !sessionExpiredHandled && isAuthenticated()) {
      sessionExpiredHandled = true;
      displayToastInfo(i18n.t('common:sessionExpired'));
      requestLogout();
      throw error;
    }

    const redirectRoute =
      backendCode !== undefined ? GLOBAL_REDIRECT_BACKEND_CODES[backendCode] : undefined;
    if (redirectRoute) {
      navigateTo(redirectRoute);
    }
    throw error;
  }
);
